'use client';
import ProductHeading from '@components/ProductHeading';
import ProductPrice from '@src/components/Product/Price';
import QuantitySelector from '@repo/ui/src/components/QuantitySelector';
import MeaterPDPHeroCarousel from '@src/components/MeaterPDPHeroCarousel';
import FeaturesContainer from '@src/components/FeaturesContainer';
import { useCart } from '@shopify/hydrogen-react';
import React from 'react';
import { useSearchParams } from 'next/navigation';
import AddToCartButton from '@src/components/AddToCartButton';
import { classNames } from '@repo/ui/src/utils/classNames';
import Markdown from '@repo/ui/src/components/Markdown';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { CartOpenOnAdd } from '@src/components/Cart/CartOpenOnAdd';
import { BreadcrumbParentProps } from '@repo/ui/src/types/breadcrumb';
import Breadcrumb from '@repo/ui/src/components/Breadcrumb';
import Seo from '@repo/ui/src/components/Seo';
import { MEATER_GEOS } from '@src/utilities/meaterGeoConfig';
import type { SeoProps, ProductProps } from '@repo/ui/src/types';
import type { MeaterProduct } from '@src/domain/types/MeaterProduct';
import type { MeaterProductSummary } from '@src/domain/types/MeaterProductSummary';
import PDPAccordion from './_PDPAccordion';
import VariantSelector from './_VariantSelector';
import CompleteSetup from './_CompleteSetup';
import ProductCollectionSelector from './_ProductCollectionSelector';
import VersatileCooking from './_VersatileCooking';
import Container from '@repo/ui/src/components/Container';
import MeaterProductNav from '@src/components/MeaterProductNav';
import DeliveryTruckIcon from '@repo/ui/src/components/Icons/DeliveryTruck';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { getGeoFromLocale } from '@src/utilities/meaterGeoConfig';
import { productAdded, productViewed } from '@repo/ui/src/services/analytics';

interface ProductDetailPageClientProps {
  product: MeaterProduct;
  initialVariantId?: string;
  breadcrumb?: BreadcrumbParentProps;
  collectionProducts?: Array<MeaterProductSummary> | null;
  relatedProducts?: Array<MeaterProduct>;
  seo?: SeoProps;
}

const ProductDetailPageClient = ({
  product,
  initialVariantId,
  breadcrumb,
  relatedProducts = [],
  collectionProducts,
  seo,
}: ProductDetailPageClientProps) => {
  const { t } = useTranslation();
  const { locale } = useCurrentUrl();
  const searchParams = useSearchParams();
  const [quantity, setQuantity] = React.useState(1);
  const { linesAdd, status, id: cartId } = useCart();
  const pendingAddRef = React.useRef(false);
  const isAdding = pendingAddRef.current && (status === 'creating' || status === 'updating');

  const activeVariantId = React.useMemo(() => {
    const param = searchParams.get('variant');
    if (param) {
      const found = product.variants.find((v) => v.variantId === param);
      if (found) return found.id;
    }
    return initialVariantId;
  }, [searchParams, product.variants, initialVariantId]);

  const activeVariant = product.variants.find((v) => v.id === activeVariantId);
  const isOutOfStock = activeVariant?.availableForSale === false;

  const carouselImages = React.useMemo(() => {
    if (!product.variantImages?.length || !activeVariant) return product.images;
    const colorValue = activeVariant.selectedOptions.find(
      (o) => o.name.toLowerCase() === 'color',
    )?.value;
    if (!colorValue) return product.images;
    const match = product.variantImages.find((vi) => vi.optionValue === colorValue);
    return match?.images ?? product.images;
  }, [product.images, product.variantImages, activeVariant]);
  
  const shippingMessage = t('productDetailPageContent.shippingMessage', undefined, {
    default: null,
  });

  const currency = MEATER_GEOS.find((g) => g.locale === locale)?.currency ?? 'USD';
  const productForSeo = {
    title: product.title,
    description: product.description,
    shortDescription: product.description,
    mainImageUrl: product.images[0]?.url,
    price: activeVariant?.price ?? product.price,
    sku: activeVariant?.sku,
    isOrderable: product.available,
    currency,
  };

  const handleAddToCart = React.useCallback(() => {
    if (!activeVariantId) return;
    pendingAddRef.current = true;
    linesAdd([{ merchandiseId: activeVariantId, quantity }]);
  }, [linesAdd, activeVariantId, quantity]);

  const geo = getGeoFromLocale(locale);
  const prevCartStatusRef = React.useRef<string>(status);

  React.useEffect(() => {
    productViewed({
      affiliation: 'website',
      basket_token: cartId?.split('/').pop() ?? null,
      brand: 'MEATER',
      currency: geo.currency,
      description: product.description ?? '',
      discount: product.discount ?? 0,
      image_url: product.previewImage?.url ?? product.images[0]?.url ?? '',
      in_stock: product.available,
      name: product.title,
      price: activeVariant?.price ?? product.price,
      product_id: activeVariant?.sku ?? product.handle,
      quantity: 1,
      sku: activeVariant?.sku,
    });
     
    // Intentional: fires once on mount as a page-view event.
    // basket_token may be empty on first-visit sessions (cart not yet created).
  }, []);

  React.useEffect(() => {
    const prev = prevCartStatusRef.current;
    prevCartStatusRef.current = status;

    if (
      pendingAddRef.current &&
      (prev === 'creating' || prev === 'updating') &&
      status === 'idle'
    ) {
      pendingAddRef.current = false;
      productAdded({
        affiliation: 'website',
        basket_token: cartId?.split('/').pop() ?? '',
        brand: 'MEATER',
        currency: geo.currency,
        products: [
          {
            image_url: product.previewImage?.url ?? product.images[0]?.url ?? '',
            name: product.title,
            price: activeVariant?.price ?? product.price,
            product_id: activeVariant?.sku ?? product.handle,
            quantity,
            sku: activeVariant?.sku,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div className={classNames('sm:relative bg-white')}>
      <MeaterProductNav
        product={product}
        activeVariantId={activeVariantId}
        onAddToCart={handleAddToCart}
        isAdding={isAdding}
        isOutOfStock={isOutOfStock}
      />
      <div className="max-w-[1440px] m-auto">
        {seo && (
          <Seo {...seo} jsonLD={{ type: 'product', product: productForSeo as ProductProps }} />
        )}
        <div className="max-w-[1440px] m-auto">
          {breadcrumb && <Breadcrumb {...breadcrumb} title={product.title} className="z-50" />}
          <Container className="pb-5">
            <div className="grid gap-y-6 gap-x-10 md:grid-cols-[1fr_calc(440px)] items-start auto-cols-fr  md:pt-20">
              <div
                className={classNames(
                  'md:sticky top-[92px] grid gap-y-6 lg:grid-flow-col md:pb-16',
                )}
              >
                {/* MOBILE HEADER */}
                <div className={classNames('md:hidden grid gap-y-2 pt-5')}>
                  <ProductHeading
                    product={product}
                    className={classNames('md:relative')}
                    titleVariant="tra-text-web-heading-3"
                    showReviews={!product.reviewsDisabled}
                  />
                  <ProductPrice
                    product={product}
                    variantId={activeVariantId}
                    component="h5"
                    variant="h5"
                    className="tra-text-web-heading-5 items-center w-auto [&_del]:text-neutral-400 [&_del]:justify-self-center"
                  />
                </div>
                <div className="-mx-4 sm:-mx-10 md:mx-0">
                  <MeaterPDPHeroCarousel images={carouselImages} />
                </div>
              </div>
              <div className={classNames('md:min-h-full')}>
                <div className="top-0 flex flex-col self-start gap-10 md:pb-16 md:sticky">
                  <div className="hidden md:block">
                    <ProductHeading
                      product={product}
                      titleVariant="tra-text-web-heading-3"
                      showReviews={!product.reviewsDisabled}
                    />
                    <ProductPrice
                      product={product}
                      variantId={activeVariantId}
                      component="h5"
                      variant="h5"
                      className="tra-text-web-heading-5 w-auto [&_del]:text-neutral-400 [&_del]:justify-self-center"
                    />
                  </div>
                  {product?.variants.length > 1 && (
                    <VariantSelector
                      variants={product.variants}
                      activeVariantId={activeVariantId}
                    />
                  )}
                  {collectionProducts && (
                    <ProductCollectionSelector
                      collectionProducts={collectionProducts}
                      activeProductId={product.id}
                    />
                  )}
                  {product?.features && <FeaturesContainer features={product.features} />}
                  {!isOutOfStock && (
                    <div className="flex flex-col gap-3">
                      <h5 className="tra-text-web-heading-5">
                        {t('cart.quantityText', undefined, { default: 'Quantity' })}
                      </h5>
                      <QuantitySelector
                        quantity={quantity}
                        handleChange={setQuantity}
                        size="medium"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-2.5" data-product-cta>
                    <CartOpenOnAdd />
                    <AddToCartButton
                      onClick={handleAddToCart}
                      disabled={!activeVariantId}
                      processing={isAdding}
                      outOfStock={isOutOfStock}
                    />
                  </div>
                  {!product.shippingDisabled && shippingMessage && (
                    <p className="flex items-center gap-2 tra-text-web-body-m text-brand-300">
                      <DeliveryTruckIcon />
                      <span>{shippingMessage}</span>
                    </p>
                  )}
                  {product?.description && (
                    <div className={classNames('text-left', 'md:bg-transparent')}>
                      <Markdown bodySize="large">{product.description}</Markdown>
                    </div>
                  )}
                  <PDPAccordion
                    featuresDescription={product.featuresDescription}
                    specs={product.specs}
                    shippingAndReturns={!product.shippingDisabled && product.shippingAndReturns}
                  />
                  {product.versatileCooking && (
                    <VersatileCooking items={product.versatileCooking} />
                  )}
                  <CompleteSetup relatedProducts={relatedProducts} />
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPageClient;
